// Broker Channel Batch (WS7a) — the Qualifications tab rebuilt as a
// sectioned, editable profile over the broker's qualification fields. Each
// section renders a read view with a per-section Edit (Brokers.Update) that
// opens a drawer form; saves go through the existing broker update API with
// the FULL hydrated payload (the FIND-12 convention BrokerEditPage uses —
// the legacy experience fields are applied unconditionally server-side, so
// a partial payload would wipe them).

import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Pencil, AlertTriangle,
  Sparkles, ChevronDown, ChevronUp, X,
} from "lucide-react";
import { ResourceDrawer } from "@/components/portfolioAdmin/loanDetail/common/ResourceDrawer";
import {
  Button,
  FormField,
  Input,
  SubmitButton,
  useSubmittingMutation,
  useToast,
} from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { Select, Textarea } from "@/components/ui/Select";
import { extractErrorMessage } from "@/api/client";
import { brokerKeys } from "@/hooks/brokers/brokerKeys";
import { useImportQualificationsFromFiles } from "@/hooks/brokers/useImportQualificationsFromFiles";
import { formatCurrency, formatNumber } from "@/design-system/lib/format";
import { useDateFormatter } from "@/utils/datetime";
import {
  brokersApi,
  type BrokerAiImportStatus,
  type BrokerDetailDto,
  type BrokerQualificationProvenanceDto,
  type UpsertBrokerPayload,
} from "@/api/brokers";

/** Approved 16-value industry-specialty taxonomy (WS7a). */
export const INDUSTRY_SPECIALTIES = [
  "Automotive",
  "Real Estate",
  "Trucking & Logistics",
  "Manufacturing",
  "Restaurants & Food Service",
  "Healthcare & Medical",
  "Construction & Trades",
  "Retail & E-commerce",
  "Professional Services",
  "Technology & Software",
  "Hospitality & Tourism",
  "Agriculture",
  "Energy & Utilities",
  "Distribution & Wholesale",
  "Personal Services",
  "Other",
] as const;

export const TRANSACTION_SIZE_RANGES = [
  "<$250k",
  "$250k–$1M",
  "$1M–$5M",
  "$5M–$25M",
  ">$25M",
] as const;

const EMPTY = "—";

/** Full-hydration payload from the detail DTO (FIND-12 — mirrors
 *  BrokerEditPage's hydrate; the legacy experience/credential fields are
 *  applied unconditionally server-side so they MUST be sent back). */
export function brokerToUpsertPayload(b: BrokerDetailDto): UpsertBrokerPayload {
  return {
    companyName: b.companyName,
    dba: b.dba ?? "",
    name: b.name ?? "",
    title: b.title ?? "",
    email: b.email ?? "",
    phone: b.phone ?? "",
    portalLoginEmail: b.portalLoginEmail ?? "",
    source: b.source,
    sourceDetail: b.sourceDetail ?? "",
    eoInsuranceProvider: b.eoInsuranceProvider ?? "",
    eoPolicyNumber: b.eoPolicyNumber ?? "",
    eoExpirationDate: b.eoExpirationDate ?? null,
    eoCoverageAmount: b.eoCoverageAmount ?? null,
    licenseNumber: b.licenseNumber ?? "",
    licenseState: b.licenseState ?? "",
    licenseExpirationDate: b.licenseExpirationDate ?? null,
    linkedInProfile: b.linkedInProfile ?? "",
    yearsOfExperience: b.yearsOfExperience ?? null,
    numberOfTransactionsClosed: b.numberOfTransactionsClosed ?? null,
    primaryAreasOfExpertise: b.primaryAreasOfExpertise ?? "",
    industriesServed: b.industriesServed ?? "",
    geographicMarketsServed: b.geographicMarketsServed ?? "",
    professionalCertificationsLicensesDesignations:
      b.professionalCertificationsLicensesDesignations ?? "",
    phoenixCapitalInterest: b.phoenixCapitalInterest ?? "",
    businessBrokerageLicenseTypes: b.businessBrokerageLicenseTypes ?? [],
    licensingDetails: b.licensingDetails ?? [],
    website: b.website ?? "",
    addressLine1: b.addressLine1 ?? "",
    addressLine2: b.addressLine2 ?? "",
    city: b.city ?? "",
    state: b.state ?? "",
    postalCode: b.postalCode ?? "",
    assignedC1LiaisonUserId: b.assignedC1LiaisonUserId ?? "",
    assignedC0ManagerUserId: b.assignedC0ManagerUserId ?? "",
    notes: b.notes ?? "",
    // WS7a fields — null = unchanged server-side, so hydrating the current
    // values keeps the payload self-consistent.
    industrySpecialties: b.industrySpecialties ?? [],
    transactionSizeRange: b.transactionSizeRange ?? "",
    largestTransactionClosed: b.largestTransactionClosed ?? null,
    totalCareerTransactionVolume: b.totalCareerTransactionVolume ?? null,
    averageDealsPerYear: b.averageDealsPerYear ?? null,
    currentActiveListings: b.currentActiveListings ?? null,
    isIbbaMember: b.isIbbaMember ?? null,
    isMaSourceMember: b.isMaSourceMember ?? null,
    otherProfessionalMemberships: b.otherProfessionalMemberships ?? "",
    professionalBiography: b.professionalBiography ?? "",
    dealSourcingNotes: b.dealSourcingNotes ?? "",
    languagesSpoken: b.languagesSpoken ?? "",
  };
}

type SectionKey = "experience" | "specialties" | "credentials" | "insurance" | "background";

export function QualificationsProfileTab({
  broker,
  canUpdate,
}: {
  broker: BrokerDetailDto;
  canUpdate: boolean;
}) {
  const { date } = useDateFormatter();
  const [editing, setEditing] = useState<SectionKey | null>(null);

  const yesNo = (v?: boolean | null) => (v === true ? "Yes" : v === false ? "No" : EMPTY);
  const text = (v?: string | null) => (v && v.trim() ? v : EMPTY);
  const num = (v?: number | null) => (v === null || v === undefined ? EMPTY : formatNumber(v));
  const money = (v?: number | null) => (v === null || v === undefined ? EMPTY : formatCurrency(v));

  // Broker "Import from files" (WS-E) — map of Broker column name → applied
  // provenance entry, so each populated field can caption its AI source.
  const provenanceByField = useMemo(
    () => buildProvenanceMap(broker.qualificationImportProvenance),
    [broker.qualificationImportProvenance],
  );

  return (
    <div className="space-y-space-4">
      {canUpdate && <ImportFromFilesSection broker={broker} />}

      {broker.applicationImportedAt && (
        <p className="text-caption text-gray-mid m-0" data-testid="profile-import-caption">
          Profile fields populated from the broker application on {date(broker.applicationImportedAt)};
          staff edits are preserved on re-approval.
        </p>
      )}
      {broker.filesImportedAt && (
        <p className="text-caption text-gray-mid m-0" data-testid="profile-files-import-caption">
          Some fields imported from filed documents on {date(broker.filesImportedAt)}.
        </p>
      )}

      <ProfileSection
        title="Experience & track record"
        sectionKey="experience"
        canUpdate={canUpdate}
        onEdit={setEditing}
      >
        <ProfileRows
          rows={[
            ["Years of experience", num(broker.yearsOfExperience), provenanceByField.get("YearsOfExperience")],
            ["Transactions closed", num(broker.numberOfTransactionsClosed), provenanceByField.get("NumberOfTransactionsClosed")],
            ["Primary areas of expertise", text(broker.primaryAreasOfExpertise), provenanceByField.get("PrimaryAreasOfExpertise")],
            ["Typical transaction size", text(broker.transactionSizeRange), provenanceByField.get("TransactionSizeRange")],
            ["Largest transaction closed", money(broker.largestTransactionClosed), provenanceByField.get("LargestTransactionClosed")],
            ["Total career transaction volume", money(broker.totalCareerTransactionVolume), provenanceByField.get("TotalCareerTransactionVolume")],
            ["Average deals per year", num(broker.averageDealsPerYear), provenanceByField.get("AverageDealsPerYear")],
            ["Current active listings", num(broker.currentActiveListings), provenanceByField.get("CurrentActiveListings")],
          ]}
        />
      </ProfileSection>

      <ProfileSection
        title="Industry specialties"
        sectionKey="specialties"
        canUpdate={canUpdate}
        onEdit={setEditing}
      >
        {(broker.industrySpecialties ?? []).length > 0 ? (
          <div className="flex flex-wrap gap-space-2">
            {(broker.industrySpecialties ?? []).map((s) => (
              <span
                key={s}
                className="rounded-pill border border-gold/40 bg-gold/10 px-space-2 py-[2px] text-caption text-onyx"
              >
                {s}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-small text-gray-mid m-0">{EMPTY}</p>
        )}
        {provenanceByField.get("IndustrySpecialties") && (
          <ProvenanceCaption entry={provenanceByField.get("IndustrySpecialties")!} />
        )}
        <p className="text-caption text-gray-mid mt-space-2 mb-0">
          Industries served (as stated on application): {text(broker.industriesServed)}
        </p>
        <ProfileRows
          rows={[["Geographic markets served", text(broker.geographicMarketsServed), provenanceByField.get("GeographicMarketsServed")]]}
        />
      </ProfileSection>

      <ProfileSection
        title="Credentials & memberships"
        sectionKey="credentials"
        canUpdate={canUpdate}
        onEdit={setEditing}
      >
        <ProfileRows
          rows={[
            ["Certifications, licenses & designations", text(broker.professionalCertificationsLicensesDesignations), provenanceByField.get("ProfessionalCertificationsLicensesDesignations")],
            ["IBBA member", yesNo(broker.isIbbaMember), provenanceByField.get("IsIbbaMember")],
            ["M&A Source member", yesNo(broker.isMaSourceMember), provenanceByField.get("IsMaSourceMember")],
            ["Other professional memberships", text(broker.otherProfessionalMemberships), provenanceByField.get("OtherProfessionalMemberships")],
          ]}
        />
        <div className="mt-space-3">
          <h4 className="text-caption font-semibold uppercase tracking-wide text-gold-shadow mb-space-1">
            Business brokerage license types
          </h4>
          {broker.businessBrokerageLicenseTypes.length > 0 ? (
            <div className="flex flex-wrap gap-space-2">
              {broker.businessBrokerageLicenseTypes.map((t) => (
                <span
                  key={t}
                  className="rounded-pill border border-gold/40 bg-gold/10 px-space-2 py-[2px] text-caption text-onyx"
                >
                  {t}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-small text-gray-mid m-0">{EMPTY}</p>
          )}
        </div>
        <div className="mt-space-3">
          <h4 className="text-caption font-semibold uppercase tracking-wide text-gold-shadow mb-space-1">
            Licensing details
          </h4>
          {broker.licensingDetails.length > 0 ? (
            <ul className="m-0 list-none p-0 space-y-space-1 text-small">
              {broker.licensingDetails.map((row, i) => (
                <li key={i}>
                  {[row.licenseTypeIssuingOrganization, row.licenseNumber, row.state, row.expirationDate]
                    .filter(Boolean)
                    .join(" · ") || EMPTY}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-small text-gray-mid m-0">{EMPTY}</p>
          )}
        </div>
      </ProfileSection>

      <ProfileSection
        title="Insurance"
        sectionKey="insurance"
        canUpdate={canUpdate}
        onEdit={setEditing}
      >
        <ProfileRows
          rows={[
            ["E&O insurance provider", text(broker.eoInsuranceProvider), provenanceByField.get("EoInsuranceProvider")],
            ["E&O policy number", text(broker.eoPolicyNumber), provenanceByField.get("EoPolicyNumber")],
            ["E&O coverage amount", money(broker.eoCoverageAmount), provenanceByField.get("EoCoverageAmount")],
            ["E&O expiration date", broker.eoExpirationDate ? date(broker.eoExpirationDate) : EMPTY, provenanceByField.get("EoExpirationDate")],
          ]}
        />
      </ProfileSection>

      <ProfileSection
        title="Background & fit"
        sectionKey="background"
        canUpdate={canUpdate}
        onEdit={setEditing}
      >
        <ProfileRows
          rows={[
            ["Professional biography", text(broker.professionalBiography), provenanceByField.get("ProfessionalBiography")],
            ["Why Phoenix Capital", text(broker.phoenixCapitalInterest), provenanceByField.get("PhoenixCapitalInterest")],
            ["Deal sourcing notes", text(broker.dealSourcingNotes), provenanceByField.get("DealSourcingNotes")],
            ["Languages spoken", text(broker.languagesSpoken), provenanceByField.get("LanguagesSpoken")],
            ["LinkedIn profile", text(broker.linkedInProfile), provenanceByField.get("LinkedInProfile")],
          ]}
        />
      </ProfileSection>

      {editing && (
        <SectionEditDrawer
          section={editing}
          broker={broker}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/* ---------------- Import from files (WS-E) ---------------- */

/** Build a Broker-column-name → entry map from the APPLIED provenance rows
 *  (last write wins per field). Skipped/unapplied rows are excluded so a
 *  field only captions its source when the value actually landed. */
function buildProvenanceMap(
  provenance: BrokerQualificationProvenanceDto[] | null | undefined,
): Map<string, BrokerQualificationProvenanceDto> {
  const map = new Map<string, BrokerQualificationProvenanceDto>();
  for (const entry of provenance ?? []) {
    if (entry.applied) map.set(entry.field, entry);
  }
  return map;
}

const IMPORT_RUNNING_STATES: BrokerAiImportStatus[] = ["Pending", "Running"];

/** The "Import from files" affordance + its lifecycle states, all driven off
 *  the broker DTO (NOT local state) so they survive navigation/refresh while
 *  the long-running AI scan polls in the background. */
function ImportFromFilesSection({ broker }: { broker: BrokerDetailDto }) {
  const toast = useToast();
  const qc = useQueryClient();
  const importMutation = useImportQualificationsFromFiles(broker.id);

  const status: BrokerAiImportStatus = broker.aiImportStatus ?? "None";
  const running = IMPORT_RUNNING_STATES.includes(status);
  const count = broker.importableDocumentCount ?? null;
  const noDocuments = count === 0;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [zeroWarning, setZeroWarning] = useState(false);
  const [skippedOpen, setSkippedOpen] = useState(false);

  // While a fresh run is in flight the banner should reappear even if the
  // user dismissed a previous one.
  useEffect(() => {
    if (running) setBannerDismissed(false);
  }, [running]);

  // Completion / failure transition detection — fire side effects exactly
  // once as the polled status flips out of a running state.
  const prevStatus = useRef<BrokerAiImportStatus>(status);
  useEffect(() => {
    const prev = prevStatus.current;
    prevStatus.current = status;
    if (prev === status) return;
    const wasRunning = IMPORT_RUNNING_STATES.includes(prev);
    if (!wasRunning) return;

    if (status === "Completed") {
      const applied = (broker.qualificationImportProvenance ?? []).filter((p) => p.applied);
      const appliedCount = applied.length;
      const distinctAppliedDocs = new Set(
        applied.map((p) => p.sourceDocumentId).filter((d): d is string => !!d),
      ).size;
      toast.success(
        `Imported ${appliedCount} field(s) from ${distinctAppliedDocs} document(s)`,
      );
      // Refresh the tab (and any other broker read) now that fields landed.
      void qc.invalidateQueries({ queryKey: brokerKeys.all });
      if ((broker.qualificationImportSkippedDocuments ?? []).length > 0) {
        setSkippedOpen(true);
      }
    } else if (status === "Failed") {
      toast.error(
        broker.aiImportFailureReason ?? "The document import failed. Please try again.",
      );
    }
  }, [status, broker, toast, qc]);

  const ZERO_WARNING_MESSAGE =
    "No documents are filed in this broker's folders yet. The application " +
    "documents need to be reviewed and saved into the broker's folders before " +
    "you can import. Once they're filed, try again.";

  const countPhrase = count !== null ? `${count} document(s)` : "the documents";
  const confirmCountPhrase = count !== null ? `these ${count}` : "these";

  const onImportClick = () => {
    if (noDocuments) {
      setZeroWarning(true);
      toast.info(ZERO_WARNING_MESSAGE);
      return;
    }
    setConfirmOpen(true);
  };

  const runImport = () => {
    setConfirmOpen(false);
    importMutation.mutate(undefined, {
      onSuccess: (result) => {
        if (result.status === "AlreadyRunning") {
          toast.info("An import is already running.");
        } else if (result.status === "NoDocuments") {
          setZeroWarning(true);
          toast.info(ZERO_WARNING_MESSAGE);
        }
        // "Started" → polling (BrokerDetailPage refetchInterval) takes over.
      },
      onError: (e) => toast.error(extractErrorMessage(e)),
    });
  };

  const skipped = broker.qualificationImportSkippedDocuments ?? [];

  return (
    <section
      className="bg-white-pure border border-border-light rounded-lg p-space-4"
      data-testid="broker-import-from-files"
    >
      <div className="flex flex-wrap items-center justify-between gap-space-2">
        <div>
          <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow m-0">
            Import from files
          </h3>
          <p className="text-caption text-gray-mid m-0 mt-space-1">
            Scan the documents filed in this broker&apos;s folders with AI to fill
            in qualification fields automatically.
          </p>
        </div>

        {running ? (
          <Button
            type="button"
            loading
            disabled
            data-testid="broker-import-running-pill"
          >
            Importing…
          </Button>
        ) : (
          <Button
            type="button"
            onClick={onImportClick}
            disabled={noDocuments || importMutation.isPending}
            title={noDocuments ? "No documents filed yet" : undefined}
            data-testid="broker-import-button"
          >
            <Sparkles size={16} aria-hidden /> Import from files
          </Button>
        )}
      </div>

      {running && !bannerDismissed && (
        <div
          className="mt-space-3 flex items-start gap-space-2 rounded-md border border-gold/40 bg-gold/10 p-space-3 text-small text-onyx"
          role="status"
          data-testid="broker-import-progress-banner"
        >
          <Sparkles size={16} className="mt-[2px] shrink-0 text-gold-shadow" aria-hidden />
          <p className="m-0 flex-1">
            Scanning this broker&apos;s filed documents with AI. This can take a few
            minutes — you can leave this page and the fields will fill in
            automatically when it&apos;s done.
          </p>
          <button
            type="button"
            onClick={() => setBannerDismissed(true)}
            aria-label="Dismiss"
            className="shrink-0 rounded-full p-space-1 hover:bg-white-warm"
          >
            <X size={14} aria-hidden />
          </button>
        </div>
      )}

      {!running && zeroWarning && (
        <div
          className="mt-space-3 flex items-start gap-space-2 rounded-md border border-status-warning/40 bg-status-warning/10 p-space-3 text-small text-onyx"
          role="alert"
          data-testid="broker-import-zero-warning"
        >
          <AlertTriangle size={16} className="mt-[2px] shrink-0 text-status-warning" aria-hidden />
          <p className="m-0 flex-1">{ZERO_WARNING_MESSAGE}</p>
        </div>
      )}

      {!running && status === "Completed" && skipped.length > 0 && (
        <div className="mt-space-3" data-testid="broker-import-skipped">
          <Button
            variant="text"
            size="sm"
            onClick={() => setSkippedOpen((open) => !open)}
            aria-expanded={skippedOpen}
          >
            {skippedOpen ? <ChevronUp size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
            {skipped.length} document(s) were skipped
          </Button>
          {skippedOpen && (
            <ul className="mt-space-2 m-0 list-none p-0 space-y-space-1 text-small">
              {skipped.map((d) => (
                <li key={d.documentId} className="flex flex-col">
                  <span className="font-semibold text-onyx">{d.fileName}</span>
                  <span className="text-caption text-gray-mid">{d.reason}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        dismissOnBackdropClick={false}
        title="Import from files"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={runImport}
              loading={importMutation.isPending}
              data-testid="broker-import-confirm"
            >
              Import
            </Button>
          </>
        }
      >
        <p className="text-small m-0">
          This will scan the {countPhrase} currently filed in this broker&apos;s
          folders. If you haven&apos;t finished reviewing and filing all of the
          application documents yet, do that first so nothing is missed. Import{" "}
          {confirmCountPhrase} now?
        </p>
      </Modal>
    </section>
  );
}

function ProfileSection({
  title,
  sectionKey,
  canUpdate,
  onEdit,
  children,
}: {
  title: string;
  sectionKey: SectionKey;
  canUpdate: boolean;
  onEdit: (s: SectionKey) => void;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white-pure border border-border-light rounded-lg p-space-4">
      <div className="flex items-center justify-between mb-space-2">
        <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow m-0">
          {title}
        </h3>
        {canUpdate && (
          <Button
            variant="text"
            size="sm"
            onClick={() => onEdit(sectionKey)}
            aria-label={`Edit ${title}`}
          >
            <Pencil size={14} aria-hidden /> Edit
          </Button>
        )}
      </div>
      {children}
    </section>
  );
}

/** A profile row: label, formatted value, and an optional applied-provenance
 *  entry whose presence renders the "source: AI from …" caption (WS-E). */
type ProfileRow = [string, string, BrokerQualificationProvenanceDto?];

function ProfileRows({ rows }: { rows: ProfileRow[] }) {
  return (
    <dl className="grid grid-cols-1 md:grid-cols-2 gap-space-2 text-small mt-space-2">
      {rows.map(([label, value, provenance]) => (
        <div key={label}>
          <dt className="text-gray-mid">{label}</dt>
          <dd className="whitespace-pre-wrap m-0">{value}</dd>
          {provenance && <ProvenanceCaption entry={provenance} />}
        </div>
      ))}
    </dl>
  );
}

/** Muted "source: AI from {document}" caption beneath an AI-imported value. */
function ProvenanceCaption({ entry }: { entry: BrokerQualificationProvenanceDto }) {
  return (
    <p
      className="text-caption text-gray-mid italic m-0 mt-[2px] flex items-center gap-space-1"
      data-testid={`profile-provenance-${entry.field}`}
    >
      <Sparkles size={11} aria-hidden />
      source: AI from {entry.sourceDocumentName ?? "a filed document"}
    </p>
  );
}

/* ---------------- Per-section edit drawer ---------------- */

// Server column limits (BrokerConfiguration) mirrored client-side so an
// over-long/over-large value is caught with a clear message instead of a
// server truncation / arithmetic-overflow 500. nvarchar(N) → maxLength
// (browser-enforced on the input); decimal(18,2) + int → range-checked
// before submit.
// decimal(18,2) tolerates ~10^16, but JS only represents integers exactly up
// to Number.MAX_SAFE_INTEGER — beyond that the entered value is itself
// unreliable, so that's the right client-side ceiling. The backend
// (BrokerService.ValidateUpsert) enforces the exact column limit.
const DECIMAL_MAX = Number.MAX_SAFE_INTEGER;
const INT_MAX = 2_147_483_647; // SQL int

const TEXT_MAX_LENGTH: Partial<Record<keyof UpsertBrokerPayload, number>> = {
  eoInsuranceProvider: 200,
  eoPolicyNumber: 100,
  primaryAreasOfExpertise: 4000,
  geographicMarketsServed: 4000,
  professionalCertificationsLicensesDesignations: 4000,
  otherProfessionalMemberships: 2000,
  professionalBiography: 4000,
  phoenixCapitalInterest: 4000,
  dealSourcingNotes: 4000,
  languagesSpoken: 200,
  linkedInProfile: 500,
};

const NUMERIC_FIELDS: ReadonlyArray<{
  key: keyof UpsertBrokerPayload;
  label: string;
  max: number;
}> = [
  { key: "yearsOfExperience", label: "Years of experience", max: INT_MAX },
  { key: "numberOfTransactionsClosed", label: "Transactions closed", max: INT_MAX },
  { key: "averageDealsPerYear", label: "Average deals per year", max: INT_MAX },
  { key: "currentActiveListings", label: "Current active listings", max: INT_MAX },
  { key: "largestTransactionClosed", label: "Largest transaction closed", max: DECIMAL_MAX },
  { key: "totalCareerTransactionVolume", label: "Total career transaction volume", max: DECIMAL_MAX },
  { key: "eoCoverageAmount", label: "E&O coverage amount", max: DECIMAL_MAX },
];

/** First out-of-range numeric field as a user-facing message, or null. */
function firstNumericError(form: UpsertBrokerPayload): string | null {
  for (const f of NUMERIC_FIELDS) {
    const v = form[f.key];
    if (typeof v === "number" && Number.isFinite(v) && (v < 0 || v > f.max)) {
      return v < 0
        ? `${f.label} cannot be negative.`
        : `${f.label} is too large (maximum ${f.max.toLocaleString()}).`;
    }
  }
  return null;
}

function SectionEditDrawer({
  section,
  broker,
  onClose,
}: {
  section: SectionKey;
  broker: BrokerDetailDto;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  // Hydrate the FULL payload once; the form edits only its section's keys.
  const [form, setForm] = useState<UpsertBrokerPayload>(() => brokerToUpsertPayload(broker));

  const set = <K extends keyof UpsertBrokerPayload>(key: K, val: UpsertBrokerPayload[K]) =>
    setForm((f) => ({ ...f, [key]: val }));

  const save = useSubmittingMutation(
    () => brokersApi.update(broker.id, form),
    {
      onSuccess: () => {
        toast.success("Profile updated.");
        void qc.invalidateQueries({ queryKey: brokerKeys.all });
        onClose();
      },
      onError: (e) => toast.error(extractErrorMessage(e)),
    },
  );

  const numberInput = (
    label: string,
    key: keyof UpsertBrokerPayload,
  ) => (
    <FormField label={label}>
      <Input
        type="number"
        aria-label={label}
        min={0}
        max={NUMERIC_FIELDS.find((f) => f.key === key)?.max}
        value={(form[key] as number | null) ?? ""}
        onChange={(e) =>
          set(key, e.target.value === "" ? null : Number(e.target.value))
        }
      />
    </FormField>
  );

  const textInput = (label: string, key: keyof UpsertBrokerPayload) => (
    <FormField label={label}>
      <Input
        aria-label={label}
        maxLength={TEXT_MAX_LENGTH[key]}
        value={(form[key] as string | null) ?? ""}
        onChange={(e) => set(key, e.target.value)}
      />
    </FormField>
  );

  const textArea = (label: string, key: keyof UpsertBrokerPayload, rows = 3) => (
    <FormField label={label}>
      <Textarea
        rows={rows}
        aria-label={label}
        maxLength={TEXT_MAX_LENGTH[key]}
        value={(form[key] as string | null) ?? ""}
        onChange={(e) => set(key, e.target.value)}
      />
    </FormField>
  );

  const yesNoSelect = (label: string, key: "isIbbaMember" | "isMaSourceMember") => (
    <FormField label={label}>
      <Select
        aria-label={label}
        value={form[key] === true ? "yes" : form[key] === false ? "no" : ""}
        onChange={(e) =>
          set(key, e.target.value === "yes" ? true : e.target.value === "no" ? false : null)
        }
      >
        <option value="">{EMPTY}</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </Select>
    </FormField>
  );

  const titles: Record<SectionKey, string> = {
    experience: "Experience & track record",
    specialties: "Industry specialties",
    credentials: "Credentials & memberships",
    insurance: "Insurance",
    background: "Background & fit",
  };

  return (
    <ResourceDrawer
      open
      onClose={onClose}
      // Don't dismiss on a scrim / outside click — an accidental click
      // shouldn't discard in-progress edits.
      dismissOnBackdropClick={false}
      title={`Edit ${titles[section]}`}
      subtitle={broker.companyName}
      testId="profile-section-drawer"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.pending}>
            Cancel
          </Button>
          <SubmitButton
            pending={save.pending}
            pendingLabel="Saving…"
            onClick={() => {
              const err = firstNumericError(form);
              if (err) {
                toast.error(err);
                return;
              }
              void save.submit();
            }}
            data-testid="profile-section-save"
          >
            Save
          </SubmitButton>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-space-3">
        {section === "experience" && (
          <>
            {numberInput("Years of experience", "yearsOfExperience")}
            {numberInput("Transactions closed", "numberOfTransactionsClosed")}
            {textArea("Primary areas of expertise", "primaryAreasOfExpertise")}
            <FormField label="Typical transaction size">
              <Select
                aria-label="Typical transaction size"
                value={form.transactionSizeRange ?? ""}
                onChange={(e) => set("transactionSizeRange", e.target.value)}
              >
                <option value="">{EMPTY}</option>
                {TRANSACTION_SIZE_RANGES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </Select>
            </FormField>
            {numberInput("Largest transaction closed (USD)", "largestTransactionClosed")}
            {numberInput("Total career transaction volume (USD)", "totalCareerTransactionVolume")}
            {numberInput("Average deals per year", "averageDealsPerYear")}
            {numberInput("Current active listings", "currentActiveListings")}
          </>
        )}

        {section === "specialties" && (
          <>
            <div>
              <div className="text-caption font-semibold uppercase tracking-wide text-gold-shadow mb-space-1">
                Industry specialties
              </div>
              <div className="flex flex-wrap gap-space-2">
                {INDUSTRY_SPECIALTIES.map((s) => {
                  const selected = (form.industrySpecialties ?? []).includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => {
                        const cur = form.industrySpecialties ?? [];
                        set(
                          "industrySpecialties",
                          selected ? cur.filter((x) => x !== s) : [...cur, s],
                        );
                      }}
                      className={
                        selected
                          ? "rounded-pill border border-gold bg-gold/15 px-space-2 py-[2px] text-caption text-onyx font-semibold"
                          : "rounded-pill border border-border-light bg-white-warm px-space-2 py-[2px] text-caption text-gray-mid hover:border-gold-shadow"
                      }
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
            <p className="text-caption text-gray-mid m-0">
              Industries served (as stated on application):{" "}
              {broker.industriesServed?.trim() || EMPTY}
            </p>
            {textArea("Geographic markets served", "geographicMarketsServed")}
          </>
        )}

        {section === "credentials" && (
          <>
            {textArea("Certifications, licenses & designations", "professionalCertificationsLicensesDesignations")}
            {yesNoSelect("IBBA member", "isIbbaMember")}
            {yesNoSelect("M&A Source member", "isMaSourceMember")}
            {textArea("Other professional memberships", "otherProfessionalMemberships")}
            <p className="text-caption text-gray-mid m-0">
              Business brokerage license types and licensing details are
              maintained on the broker edit form.
            </p>
          </>
        )}

        {section === "insurance" && (
          <>
            {textInput("E&O insurance provider", "eoInsuranceProvider")}
            {textInput("E&O policy number", "eoPolicyNumber")}
            {numberInput("E&O coverage amount (USD)", "eoCoverageAmount")}
            <FormField label="E&O expiration date">
              <Input
                type="date"
                aria-label="E&O expiration date"
                value={form.eoExpirationDate ?? ""}
                onChange={(e) => set("eoExpirationDate", e.target.value || null)}
              />
            </FormField>
          </>
        )}

        {section === "background" && (
          <>
            {textArea("Professional biography", "professionalBiography", 5)}
            {textArea("Why Phoenix Capital", "phoenixCapitalInterest")}
            {textArea("Deal sourcing notes", "dealSourcingNotes")}
            {textInput("Languages spoken", "languagesSpoken")}
            {textInput("LinkedIn profile", "linkedInProfile")}
          </>
        )}
      </div>
    </ResourceDrawer>
  );
}

